// client/src/coms/organisation/SearchView.js
// Universal search: one box with operators, category tabs, scope (everywhere / this server / this channel /
// a DM), an advanced-filter panel that writes the same operators, autocomplete for filters, people and
// channels, highlighted snippets and paging. Results open the original item (messages jump to the message,
// loading older history as needed). The server applies every permission check; this view just renders.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { apiGetJson } from './orgApi';
import { formatBytes, formatDate, ProviderBadge, kindIcon, Spinner, EmptyState, openTarget } from './orgShared';
import { saveBookmark } from './bookmarkActions';

const CATEGORIES = [
  ['all', 'All'],
  ['messages', 'Messages'],
  ['dms', 'Direct messages'],
  ['files', 'Files'],
  ['channels', 'Channels'],
  ['servers', 'Servers'],
  ['resources', 'Resources'],
  ['bookmarks', 'Bookmarks'],
  ['canvas', 'Canvas'],
];
const CATEGORY_TITLES = Object.fromEntries(CATEGORIES);
const BOOKMARK_TYPE = { message: 'message', dm_message: 'dm_message', file: 'file', attachment: 'attachment', external: 'external', channel: 'channel', server: 'server', resource: 'resource', canvas: 'canvas' };

function escapeRe(s) {
  return String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export function Highlight({ text, terms }) {
  const list = (terms || []).filter((t) => t && t.length > 1).slice(0, 12);
  if (!text || !list.length) return text || null;
  const re = new RegExp(`(${list.map(escapeRe).join('|')})`, 'gi');
  const parts = String(text).split(re);
  return parts.map((p, i) => (i % 2 ? <mark key={i}>{p}</mark> : <React.Fragment key={i}>{p}</React.Fragment>));
}

export default function SearchView({ server, channel, initial }) {
  const serverKey = server && server.id != null ? String(server.id) : '';
  const [q, setQ] = useState((initial && initial.q) || '');
  const [submitted, setSubmitted] = useState((initial && initial.q) || '');
  const [category, setCategory] = useState((initial && initial.category) || 'all');
  const [scope, setScope] = useState((initial && initial.scope) || (serverKey ? 'server' : 'global'));
  const [sort, setSort] = useState('relevance');
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [advanced, setAdvanced] = useState(false);
  const [sugg, setSugg] = useState([]);
  const [suggIdx, setSuggIdx] = useState(-1);
  const inputRef = useRef(null);
  const runSeq = useRef(0);
  const channelDbId = channel && channel.kind === 'text' ? channel.db_id || String(channel.id || '').replace(/^\D+/, '') : '';
  const dmThread = initial && initial.dm ? String(initial.dm) : '';

  useEffect(() => {
    const t = setTimeout(() => inputRef.current && inputRef.current.focus(), 50);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const on = (e) => {
      const d = e.detail || {};
      if (d.area !== 'search') return;
      if (d.q != null) {
        setQ(d.q);
        setSubmitted(d.q);
      }
      if (d.scope) setScope(d.scope);
      if (d.category) setCategory(d.category);
      setTimeout(() => inputRef.current && inputRef.current.focus(), 30);
    };
    window.addEventListener('vox-open-org', on);
    return () => window.removeEventListener('vox-open-org', on);
  }, []);

  const run = useCallback(
    async (cursor = 0) => {
      const text = submitted.trim();
      if (!text && category !== 'bookmarks' && scope !== 'channel' && scope !== 'dm') {
        setRes(null);
        return;
      }
      const mine = ++runSeq.current;
      setLoading(true);
      setErr(null);
      try {
        const qs = new URLSearchParams({ q: text, category, sort, scope });
        if ((scope === 'server' || scope === 'channel') && serverKey) qs.set('server', serverKey);
        if (scope === 'channel' && channelDbId) qs.set('channel', String(channelDbId));
        if (scope === 'dm' && dmThread) qs.set('dm', dmThread);
        if (cursor) qs.set('cursor', String(cursor));
        const r = await apiGetJson(`/search?${qs}`);
        if (mine !== runSeq.current) return;
        setRes((prev) => (cursor && prev && prev.results ? { ...r, results: [...prev.results, ...(r.results || [])] } : r));
      } catch (e) {
        if (mine !== runSeq.current) return;
        setErr(e);
        if (!cursor) setRes(null);
      } finally {
        if (mine === runSeq.current) setLoading(false);
      }
    },
    [submitted, category, sort, scope, serverKey, channelDbId, dmThread]
  );
  useEffect(() => {
    run(0);
  }, [run]);

  // Autocomplete for the token under the cursor.
  useEffect(() => {
    const el = inputRef.current;
    const pos = el ? el.selectionStart : q.length;
    const before = q.slice(0, pos);
    const m = /(?:^|\s)(-?[a-z]*:?[^\s]*)$/i.exec(before);
    const tok = m ? m[1] : '';
    if (!tok || (!tok.includes(':') && tok.length < 2)) {
      setSugg([]);
      return undefined;
    }
    const t = setTimeout(() => {
      apiGetJson(`/search/suggest?q=${encodeURIComponent(tok.replace(/^-/, ''))}${serverKey && scope !== 'global' ? `&server=${encodeURIComponent(serverKey)}` : ''}`).then(
        (r) => {
          setSugg((r.suggestions || []).filter((s) => s.value !== tok).slice(0, 8));
          setSuggIdx(-1);
        },
        () => setSugg([])
      );
    }, 150);
    return () => clearTimeout(t);
  }, [q, serverKey, scope]);

  function applySuggestion(s) {
    const el = inputRef.current;
    const pos = el ? el.selectionStart : q.length;
    const before = q.slice(0, pos).replace(/(-?)[^\s]*$/, (all, neg) => `${neg}${s.value}`);
    const next = `${before}${s.value.endsWith(':') ? '' : ' '}${q.slice(pos).replace(/^\S*/, '')}`;
    setQ(next);
    setSugg([]);
    setTimeout(() => {
      if (el) {
        el.focus();
        el.setSelectionRange(before.length + (s.value.endsWith(':') ? 0 : 1), before.length + (s.value.endsWith(':') ? 0 : 1));
      }
    }, 0);
  }

  function onKeyDown(e) {
    if (sugg.length && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      e.preventDefault();
      setSuggIdx((i) => (i + (e.key === 'ArrowDown' ? 1 : -1) + sugg.length) % sugg.length);
      return;
    }
    if (sugg.length && suggIdx >= 0 && (e.key === 'Enter' || e.key === 'Tab')) {
      e.preventDefault();
      applySuggestion(sugg[suggIdx]);
      return;
    }
    if (e.key === 'Escape') setSugg([]);
    if (e.key === 'Enter') {
      setSugg([]);
      setSubmitted(q);
    }
  }

  const terms = (res && res.query && res.query.terms) || [];
  const sections = res && res.sections;
  const results = res && res.results;

  return (
    <div className="coms-view vxo-view vx-pz" data-testid="search-view">
      <header className="vxo-head">
        <div className="vxo-head-title">
          <h1>Search</h1>
        </div>
      </header>
      <div className="vxo-searchbar">
        <div className="vxo-search-input-wrap">
          <span aria-hidden="true">🔎</span>
          <input
            ref={inputRef}
            className="vxo-search-input"
            type="search"
            role="combobox"
            aria-expanded={sugg.length > 0}
            aria-controls="vxo-search-suggestions"
            aria-activedescendant={suggIdx >= 0 ? `vxo-sugg-${suggIdx}` : undefined}
            aria-label="Search messages, files, channels and more"
            placeholder='Try: dragon from:ann in:general after:2026-09-01 "patch notes" -spoilers'
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKeyDown}
            data-testid="search-input"
          />
          <button type="button" className="vx-btn primary" onClick={() => setSubmitted(q)} data-testid="search-submit">
            Search
          </button>
          {sugg.length ? (
            <ul id="vxo-search-suggestions" className="vxo-suggest" role="listbox">
              {sugg.map((s, i) => (
                <li key={s.value} id={`vxo-sugg-${i}`} role="option" aria-selected={i === suggIdx} className={i === suggIdx ? 'is-active' : ''} onMouseDown={(e) => {
                  e.preventDefault();
                  applySuggestion(s);
                }}>
                  <span className="vxo-muted">{s.kind}</span> {s.label}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
        <div className="vxo-search-controls">
          <select className="vxo-select" aria-label="Where to search" value={scope} onChange={(e) => setScope(e.target.value)} data-testid="search-scope">
            <option value="global">Everywhere</option>
            {serverKey ? <option value="server">This server{server && server.name ? ` (${server.name})` : ''}</option> : null}
            {serverKey && channelDbId ? <option value="channel">This channel (#{channel.name})</option> : null}
            {dmThread ? <option value="dm">This conversation</option> : null}
          </select>
          <select className="vxo-select" aria-label="Sort" value={sort} onChange={(e) => setSort(e.target.value)}>
            <option value="relevance">Best match</option>
            <option value="newest">Newest</option>
            <option value="oldest">Oldest</option>
          </select>
          <button type="button" className={`vx-btn${advanced ? ' is-on' : ''}`} aria-expanded={advanced} onClick={() => setAdvanced((v) => !v)}>
            Filters
          </button>
        </div>
      </div>
      {advanced ? <AdvancedFilters onApply={(add) => {
        const next = `${q.trim()} ${add}`.trim();
        setQ(next);
        setSubmitted(next);
      }} /> : null}
      <div className="vxo-tabs vxo-search-tabs" role="tablist" aria-label="Result type">
        {CATEGORIES.map(([id, label]) => (
          <button key={id} type="button" role="tab" aria-selected={category === id} className={`vxo-tab${category === id ? ' is-active' : ''}`} onClick={() => setCategory(id)}>
            {label}
          </button>
        ))}
      </div>
      <div className="vxo-results" aria-live="polite" aria-busy={loading}>
        {err ? (
          <div className="vxo-error" role="alert">
            {err.message}
            {err.data && err.data.code === 'bad_query' ? <span className="vxo-muted"> · see “Search tips” below</span> : null}
          </div>
        ) : null}
        {loading && !res ? <Spinner label="Searching…" /> : null}
        {!res && !loading && !err ? <SearchTips /> : null}
        {res && res.empty_query ? <SearchTips /> : null}
        {sections && !sections.length ? <EmptyState icon="🔍" title="No results">Try fewer words, another spelling, or search everywhere.</EmptyState> : null}
        {sections
          ? sections.map((s) => (
              <section key={s.category} className="vxo-result-section">
                <h2 className="vxo-h3">
                  {CATEGORY_TITLES[s.category]}
                  {s.more ? (
                    <button type="button" className="vxo-linkbtn" onClick={() => setCategory(s.category)}>
                      See all
                    </button>
                  ) : null}
                </h2>
                <ul className="vxo-result-list">
                  {s.items.map((it) => (
                    <ResultItem key={`${it.type}-${it.id}`} it={it} terms={terms} />
                  ))}
                </ul>
              </section>
            ))
          : null}
        {results && !results.length ? <EmptyState icon="🔍" title="No results" /> : null}
        {results ? (
          <ul className="vxo-result-list">
            {results.map((it) => (
              <ResultItem key={`${it.type}-${it.id}`} it={it} terms={terms} />
            ))}
          </ul>
        ) : null}
        {res && res.next_cursor ? (
          <button type="button" className="vx-btn vxo-more" disabled={loading} onClick={() => run(res.next_cursor)}>
            {loading ? 'Loading…' : 'More results'}
          </button>
        ) : null}
        {res && res.took_ms != null ? <p className="vxo-muted vxo-took">Searched in {res.took_ms} ms</p> : null}
      </div>
    </div>
  );
}

function ResultItem({ it, terms }) {
  const icon = it.type === 'message' ? '💬' : it.type === 'dm_message' ? '✉️' : it.type === 'channel' ? '#' : it.type === 'server' ? '🏠' : it.type === 'resource' ? '📚' : it.type === 'bookmark' ? '🔖' : it.type === 'canvas' ? '🎨' : kindIcon(it.mime, { folder: it.is_folder, kind: it.kind });
  const bt = BOOKMARK_TYPE[it.type];
  return (
    <li className="vxo-result">
      <button type="button" className="vxo-result-main" onClick={() => openTarget(it.open)} data-testid="search-result">
        <span className="vxo-result-icon" aria-hidden="true">
          {icon}
        </span>
        <span className="vxo-result-body">
          <span className="vxo-result-title">
            {it.provider ? <ProviderBadge provider={it.provider} /> : null}
            <Highlight text={it.title} terms={terms} />
            {it.channel_archived || it.archived ? <span className="vxo-badge">archived</span> : null}
            {it.pinned ? <span className="vxo-badge">pinned</span> : null}
            {it.private ? <span className="vxo-badge">private</span> : null}
          </span>
          {it.snippet ? (
            <span className="vxo-result-snippet">
              <Highlight text={it.snippet} terms={terms} />
            </span>
          ) : null}
          <span className="vxo-result-meta">
            {[it.source, it.author ? `by ${it.author}` : null, it.size != null && it.type !== 'message' ? formatBytes(it.size) : null, it.created_at ? formatDate(it.created_at) : null, it.attachments && it.attachments.length ? '📎 attachment' : null, it.path || null].filter(Boolean).join(' · ')}
          </span>
        </span>
      </button>
      {bt ? (
        <button type="button" className="vx-icon-btn" aria-label="Save bookmark" title="Save bookmark" onClick={() => saveBookmark(bt, it.type === 'channel' ? `text:${it.id}` : it.id)}>
          🔖
        </button>
      ) : null}
    </li>
  );
}

function AdvancedFilters({ onApply }) {
  const [f, setF] = useState({ from: '', in: '', after: '', before: '', has: '', type: '', exact: '', exclude: '', pinned: false, bookmarked: false, interaction: '', istate: '' });
  const set = (p) => setF((x) => ({ ...x, ...p }));
  const quote = (v) => (/\s/.test(v) ? `"${v.replace(/"/g, '')}"` : v);
  const build = useMemo(
    () =>
      [
        f.exact ? `"${f.exact.replace(/"/g, '')}"` : '',
        f.from ? `from:${quote(f.from)}` : '',
        f.in ? `in:${quote(f.in.replace(/^#/, ''))}` : '',
        f.after ? `after:${f.after}` : '',
        f.before ? `before:${f.before}` : '',
        f.has ? `has:${f.has}` : '',
        f.type ? `type:${f.type}` : '',
        ...f.exclude.split(/\s+/).filter(Boolean).map((w) => `-${w}`),
        f.pinned ? 'is:pinned' : '',
        f.bookmarked ? 'is:bookmarked' : '',
        f.interaction ? `interaction:${f.interaction}` : '',
        f.istate ? `is:${f.istate}` : '',
      ]
        .filter(Boolean)
        .join(' '),
    [f]
  );
  return (
    <form
      className="vxo-advanced"
      onSubmit={(e) => {
        e.preventDefault();
        if (build) onApply(build);
      }}
    >
      <label className="vxo-field">
        <span>From</span>
        <input value={f.from} onChange={(e) => set({ from: e.target.value })} placeholder="username or me" />
      </label>
      <label className="vxo-field">
        <span>In channel</span>
        <input value={f.in} onChange={(e) => set({ in: e.target.value })} placeholder="general" />
      </label>
      <label className="vxo-field">
        <span>After</span>
        <input type="date" value={f.after} onChange={(e) => set({ after: e.target.value })} />
      </label>
      <label className="vxo-field">
        <span>Before</span>
        <input type="date" value={f.before} onChange={(e) => set({ before: e.target.value })} />
      </label>
      <label className="vxo-field">
        <span>Interaction</span>
        <select value={f.interaction} onChange={(e) => set({ interaction: e.target.value })}>
          <option value="">Any message</option>
          <option value="any">Any interaction</option>
          <option value="poll">Polls</option>
          <option value="event">Events</option>
          <option value="decision">Decisions</option>
          <option value="tasks">Task lists</option>
          <option value="qa">Q&amp;A sessions</option>
          <option value="suggestion">Suggestions</option>
          <option value="countdown">Countdowns</option>
        </select>
      </label>
      <label className="vxo-field">
        <span>Interaction state</span>
        <select value={f.istate} onChange={(e) => set({ istate: e.target.value })}>
          <option value="">Any</option>
          <option value="active">Active</option>
          <option value="closed">Closed</option>
          <option value="upcoming">Upcoming (events, countdowns)</option>
          <option value="open">Open tasks</option>
        </select>
      </label>
      <label className="vxo-field">
        <span>Has</span>
        <select value={f.has} onChange={(e) => set({ has: e.target.value })}>
          <option value="">Anything</option>
          <option value="file">A file</option>
          <option value="image">An image</option>
          <option value="video">A video</option>
          <option value="audio">Audio</option>
          <option value="link">A link</option>
        </select>
      </label>
      <label className="vxo-field">
        <span>File type</span>
        <select value={f.type} onChange={(e) => set({ type: e.target.value })}>
          <option value="">Any</option>
          {['image', 'video', 'audio', 'pdf', 'document', 'archive', 'text', 'folder'].map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      </label>
      <label className="vxo-field">
        <span>Exact phrase</span>
        <input value={f.exact} onChange={(e) => set({ exact: e.target.value })} />
      </label>
      <label className="vxo-field">
        <span>Without the words</span>
        <input value={f.exclude} onChange={(e) => set({ exclude: e.target.value })} />
      </label>
      <label className="vxo-check">
        <input type="checkbox" checked={f.pinned} onChange={(e) => set({ pinned: e.target.checked })} /> <span>Pinned messages</span>
      </label>
      <label className="vxo-check">
        <input type="checkbox" checked={f.bookmarked} onChange={(e) => set({ bookmarked: e.target.checked })} /> <span>Bookmarked by me</span>
      </label>
      <div className="vxo-advanced-foot">
        <code className="vxo-muted">{build || 'Choose filters…'}</code>
        <button type="submit" className="vx-btn primary" disabled={!build}>
          Add to search
        </button>
      </div>
    </form>
  );
}

function SearchTips() {
  return (
    <div className="vxo-tips">
      <h2 className="vxo-h3">Search tips</h2>
      <ul className="vxo-plain">
        <li>
          <code>dragon map</code> — both words; <code>dragon OR wyvern</code> — either; <code>dragon NOT spoilers</code> or <code>dragon -spoilers</code> — exclude
        </li>
        <li>
          <code>"patch notes"</code> — exact phrase; <code>drag*</code> — words starting with “drag”; <code>(map OR atlas) dragon</code> — grouping
        </li>
        <li>
          <code>from:ann</code> <code>from:me</code> <code>in:general</code> <code>server:"Game Night"</code> <code>before:2026-10-01</code> <code>after:2026-09-01</code> <code>on:2026-09-15</code>
        </li>
        <li>
          <code>has:file</code> <code>has:image</code> <code>has:link</code> <code>type:pdf</code> <code>type:image</code> <code>is:pinned</code> <code>is:bookmarked</code>
        </li>
        <li>Operators AND, OR and NOT must be in capitals. NOT binds tightest, then AND (also implied by a space), then OR.</li>
        <li>Shortcut: Ctrl+K (⌘K on Mac) opens search from anywhere.</li>
      </ul>
    </div>
  );
}
